import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import uploadIcon from "../../Assets/Images/feather-upload-cloud.svg";
import "../../Assets/Styles/pages/pickImage.scss";
import "../../Assets/Styles/pages/homeRedesign.scss";

import { setExtension } from "../../Redux/DownloadSlice/downloadSlice";
import { clearBatchUrls, setImageUrl, setLoader } from "../../Redux/MainImage/imageSlice";
import {
  setCreate_Template,
  setID,
  setTemplateAuthoringAspectPreset,
} from "../../Redux/UserSlice/userSlice";
import { deleteAll } from "../../Redux/WaterMarks/watermarks";
import ImageSourceSelectorModal from "../Modals/ImageSourceSelectorModal";
import { TEMPLATE_DEMO_BACKGROUND_URL } from "../../Config/templateAspectRatios";

import {
  getBase64FromFile,
  extractExifFromBase64,
  validateImageExtension,
  convertHeicToJpeg,
} from "../../Utils/imageUtils";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

interface UploadSingleImageButtonProps {
  cause?: "newTemplate";
}

const UploadSingleImageButton = ({
  cause,
}: UploadSingleImageButtonProps) => {
  const history = useHistory();
  const [dragging, setDragging] = useState(false);
  const [openSelectImageModal, setOpenSelectImageModal] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleFileDrop = async (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    setDragging(false);

    const files = event.dataTransfer.files;
    if (files.length > 1) {
      alert("Only one image is allowed.");
      return;
    }

    let file = files[0];
    const ext = validateImageExtension(file.name);
    if (!ext) {
      alert(t("NotSuportedFile"));
      return;
    }

    if (ext === "heic" || ext === "heif") {
      try {
        file = await convertHeicToJpeg(file);
      } catch (err) {
        alert("Failed to convert HEIC/HEIF image. Please try a different image.");
        console.error("HEIC conversion error:", err);
        return;
      }
    }

    let base64: string;
    let exifObj: Record<string, unknown> = {};
    try {
      base64 = (await getBase64FromFile(file)) as string;
      exifObj = extractExifFromBase64(base64);
    } catch (err) {
      console.log("No metaData:", err);
      return;
    }

    // Non-square uploads are fine for `newTemplate` now: the aspect bar lets the user
    // reshape the canvas, and the template save path always remaps to 1:1 before serialising.

    const newExt = validateImageExtension(file.name);
    const finalExt = ext === "gif" ? "jpeg" : (typeof newExt === "string" ? newExt : "jpeg");

    dispatch(setExtension(finalExt));
    dispatch(setLoader(true));
    dispatch(
      setImageUrl({
        url: base64,
        name: file.name,
        metadata: JSON.stringify(exifObj),
      })
    );

    if (cause === "newTemplate") {
      dispatch(setCreate_Template(true));
      dispatch(setTemplateAuthoringAspectPreset("1:1"));
      // Starting a brand new template should not keep old template watermarks.
      dispatch(deleteAll());
      dispatch(setID(null));
      // A brand new template is a single-image session. `mainImage` isn't persisted but it does
      // survive SPA navigation, so a stale non-empty `batchurls` from an earlier batch session
      // would make `initializeMainImage` take its batch branch and never show this base image.
      dispatch(clearBatchUrls());
    }
    history.push("/edit");
    dispatch(setLoader(false));
  };

  /**
   * Skip the upload step entirely and enter the template editor with the bundled demo
   * background. The user then picks an aspect ratio from the ratio bar. Template save
   * will programmatically remap to 1:1 so persisted values still match the legacy flow.
   */
  const handleStartWithSampleBackground = () => {
    dispatch(setLoader(true));
    dispatch(setExtension("png"));
    dispatch(
      setImageUrl({
        url: TEMPLATE_DEMO_BACKGROUND_URL,
        name: "canvasBgDemo.png",
        metadata: JSON.stringify({}),
      })
    );
    dispatch(setCreate_Template(true));
    dispatch(setTemplateAuthoringAspectPreset("1:1"));
    dispatch(deleteAll());
    dispatch(setID(null));
    // See `handleFileDrop` above -- a stale `batchurls` from an earlier batch session otherwise
    // routes this single-image template flow through `initializeMainImage`'s batch branch and
    // the demo background never renders.
    dispatch(clearBatchUrls());
    history.push("/edit");
    dispatch(setLoader(false));
  };

  const handleClose = () => {
    setOpenSelectImageModal(false);
  };

  return (
    <>
      {openSelectImageModal && (
        <ImageSourceSelectorModal
          open={openSelectImageModal}
          onCloseAndClean={handleClose}
          uploadImageType={'main'}
          multiple={false}
          cause={cause}
          autoTriggerFilePicker
        />
      )}

      <div
        onDrop={handleFileDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        className={
          cause === "newTemplate"
            ? "pickImage w_38 w_xl_30 px-4 pt-3 pb-4 pickImageTemplate"
            : "home-upload-box"
        }
      >
        {cause === "newTemplate" ? (
          <div
            className={`dashed_container mt-1 py-5 ${dragging ? "draggingImage" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleStartWithSampleBackground();
            }}
          >
            <div className="upload_title productSansMedium text_18 mb-1">
              {t("lang.upload_title_create_template")}
            </div>
          </div>
        ) : (
          <div
            className={`home-upload-box__dashed ${dragging ? "draggingImage" : ""}`}
            onClick={() => setOpenSelectImageModal(true)}
          >
            <div className="home-upload-box__icon-circle">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M7 16a4 4 0 0 1-.88-7.9 5 5 0 0 1 9.76-1.63A4.5 4.5 0 0 1 17 15.5" stroke="#F36573" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 12v7m0-7 2.5 2.5M12 12 9.5 14.5" stroke="#F36573" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="home-upload-box__title">Drag &amp; drop an image here</div>
            <div className="home-upload-box__or">or</div>
            <button
              type="button"
              className="home-upload-box__choose-btn"
              onClick={(e) => {
                e.stopPropagation();
                setOpenSelectImageModal(true);
              }}
            >
              <img alt="" src={uploadIcon} className="home-upload-box__choose-icon" />
              Choose Image
            </button>
            <div className="home-upload-box__caption">Supports JPG • PNG • WEBP • HEIC • GIF</div>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadSingleImageButton;
